import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import styles from './ProPlanTransactionsList.module.css';
import { usePermission } from '../../hooks/usePermissions';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import AddProPlanTransaction from './AddProPlanTransaction';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const SendIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const ProPlanTransactionsList = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);

    // Pagination and Filtering State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    // Sorting
    const [sortKey, setSortKey] = useState('pro_plans_transaction_id');
    const [sortOrder, setSortOrder] = useState('desc');

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };


    const fetchTransactions = (page = currentPage) => {
        setLoading(true);
        api.get('/proplantransaction')
            .then((response) => {
                let resultsArray = response?.result || response?.data || response || [];
                if (!Array.isArray(resultsArray)) resultsArray = [];

                // Frontend filtering
                let filtered = [...resultsArray];

                // Search filter
                if (searchTerm) {
                    const lSearch = searchTerm.toLowerCase();
                    filtered = filtered.filter(item => {
                        const hasName = item.user_name && item.user_name.toLowerCase().includes(lSearch);
                        const hasTxId = item.transaction_id && item.transaction_id.toLowerCase().includes(lSearch);
                        const hasUserId = item.user_id && item.user_id.toString().includes(lSearch);
                        const hasProId = item.pro_plans_transaction_id && item.pro_plans_transaction_id.toString().includes(lSearch);
                        return hasName || hasTxId || hasUserId || hasProId;
                    });
                }

                // Date range filter
                if (fromDate) {
                    const from = new Date(fromDate);
                    filtered = filtered.filter(item => {
                        const d = new Date(item.plan_activation_date);
                        return d >= from;
                    });
                }
                if (toDate) {
                    const to = new Date(toDate);
                    to.setHours(23, 59, 59, 999);
                    filtered = filtered.filter(item => {
                        const d = new Date(item.plan_activation_date);
                        return d <= to;
                    });
                }

                // Sorting
                filtered.sort((a, b) => {
                    let aVal = a[sortKey];
                    let bVal = b[sortKey];
                    if (aVal === null || aVal === undefined) aVal = '';
                    if (bVal === null || bVal === undefined) bVal = '';
                    const aNum = Number(aVal);
                    const bNum = Number(bVal);
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
                    }
                    return sortOrder === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal));
                });

                // Frontend pagination
                const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage) || 1;
                let safePage = page;
                if (safePage > totalFilteredPages) {
                    safePage = totalFilteredPages;
                    setCurrentPage(safePage);
                }
                const startIndex = (safePage - 1) * itemsPerPage;
                const sliced = filtered.slice(startIndex, startIndex + itemsPerPage);

                setTransactions(sliced);
                setTotalPages(totalFilteredPages);
            })
            .catch((error) => {
                console.error('Error fetching pro plan transactions:', error);
                const errMsg = error.response?.data?.message || error.message || 'Failed to fetch';
                toast.error(`Fetch Error: ${errMsg}`);
                setTransactions([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchTransactions(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortKey, sortOrder]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchTransactions(1);
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) return;
        api.delete(`/proplantransaction/${id}`)
            .then(() => {
                toast.success('Transaction deleted successfully');
                fetchTransactions(currentPage);
            })
            .catch((error) => {
                const errMsg = error.response?.data?.message || error.message || 'Failed to delete';
                toast.error(`Delete Error: ${errMsg}`);
            });
    };

    const handleResend = async (id) => {
        if (!window.confirm("Resend this notification?")) return;
        try {
            await api.post(`/proplantransaction/${id}/resend`);
            toast.success("Notification resent successfully!");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to resend");
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Pro Plan Transactions</h1>
                {hasPermission('CREATE_PRO_PLAN_TRANSACTIONS') && (
                    <button
                        className={styles.addBtn}
                        onClick={() => {
                            setSelectedTransactionId(null);
                            setIsModalOpen(true);
                        }}
                    >
                        ➕ Add Pro Plan
                    </button>
                )}
            </div>

            <div className={styles.card}>
                {/* Filters Bar */}
                <div className={styles.filtersBar}>
                    <div className={styles.dateFilters}>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>From</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={fromDate}
                                onChange={e => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className={styles.dateGroup}>
                            <label className={styles.dateLabel}>To</label>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={toDate}
                                onChange={e => setToDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className={styles.searchGroup}>
                        <label className={styles.dateLabel}>Search</label>
                        <div className={styles.searchInputRow}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by ID, User, TX..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleSearch()}
                            />
                            <button className={styles.searchBtn} onClick={handleSearch}>
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            <span>Loading transactions...</span>
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('pro_plans_transaction_id')} className={styles.sortable}>Pro Plan ID {sortKey === 'pro_plans_transaction_id' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('user_id')} className={styles.sortable}>User ID {sortKey === 'user_id' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('user_name')} className={styles.sortable}>User Name {sortKey === 'user_name' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th>RP TX ID</th>
                                    <th>Internal TX ID</th>
                                    <th onClick={() => handleSort('plan_id')} className={styles.sortable}>Plan ID {sortKey === 'plan_id' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th onClick={() => handleSort('plan_status')} className={styles.sortable}>Status {sortKey === 'plan_status' ? <span className={styles.sortIconActive}>{sortOrder === 'asc' ? '⇅' : '⇅'}</span> : <span className={styles.sortIcon}>⇅</span>}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.length > 0 ? (
                                    transactions.map((row, idx) => (
                                        <tr key={row.pro_plans_transaction_id ?? idx}>
                                            <td>{row.pro_plans_transaction_id}</td>
                                            <td>{row.user_id}</td>
                                            <td>{row.user_name || 'N/A'}</td>
                                            <td className={styles.txId}>{row.transaction_id || ''}</td>
                                            <td>{row.internal_transaction_id || ''}</td>
                                            <td>{row.plan_id}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${row.plan_status?.toLowerCase() === 'active'
                                                    ? styles.statusActive
                                                    : styles.statusInactive
                                                    }`}>
                                                    {row.plan_status?.toUpperCase() || 'UNKNOWN'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    {hasPermission('MODIFY_PRO_PLAN_TRANSACTIONS') && (
                                                        <button
                                                            className={styles.editBtn}
                                                            title="Edit"
                                                            onClick={() => {
                                                                setSelectedTransactionId(row.pro_plans_transaction_id);
                                                                setIsModalOpen(true);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    )}
                                                    {(row.status?.toLowerCase() === 'success' || row.plan_status?.toLowerCase() === 'active') && (
                                                        <button
                                                            className={styles.editBtn}
                                                            style={{ color: "#10b981", borderColor: "#10b981" }}
                                                            title="Resend Notification"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleResend(row.transaction_id || row.internal_transaction_id || row.pro_plans_transaction_id);
                                                            }}
                                                        >
                                                            <SendIcon />
                                                        </button>
                                                    )}
                                                    {hasPermission('DELETE_PRO_PLAN_TRANSACTIONS') && (
                                                        <button
                                                            className={styles.deleteBtn}
                                                            title="Delete"
                                                            onClick={() => handleDelete(row.pro_plans_transaction_id)}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className={styles.emptyState}>
                                            No transactions found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && transactions.length > 0 && (
                    <div className={styles.pagination}>
                        <div className={styles.paginationInfo}>
                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, transactions.length * totalPages)} of {transactions.length * totalPages} entries
                        </div>
                        <div className={styles.paginationControls}>
                            <button
                                className={styles.pageBtn}
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                &lt;
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={page}
                                            className={`${styles.pageBtn} ${currentPage === page ? styles.active : ""}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    );
                                }

                                if (page === currentPage - 2 || page === currentPage + 2) {
                                    return (
                                        <span key={page} className={styles.paginationEllipsis}>
                                            ...
                                        </span>
                                    );
                                }

                                return null;
                            })}

                            <button
                                className={styles.pageBtn}
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                &gt;
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedTransactionId ? "Edit Pro Plan Transaction" : "Create Pro Plan Transaction"}
                    maxWidth="800px"
                    disableBackdropClick={true}
                >
                    <AddProPlanTransaction
                        transactionId={selectedTransactionId}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchTransactions(currentPage);
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    );
};

export default ProPlanTransactionsList;
